/**
 * Presets de interface por org (white-label).
 *
 * `organizations.ui_preset` (migration 20260930100000) diz QUAL preset a org usa; o que cada
 * preset muda mora aqui. `null` = Vertto padrão, e nenhum ponto do app muda de comportamento.
 *
 * Um preset tem duas partes:
 *   - marca (sempre): tema (atributo `data-brand` no <html>, tokens em
 *     src/styles/themes/<brand>.css), fontes, favicon, logos e telas de carregamento;
 *   - `shell` (opcional): menu enxuto, bloqueio de rota (OrgPresetRouteGuard), abas de
 *     Configurações filtradas, sem telefone no header e sem tours.
 *   - `ajustes` (opcional, só para preset sem shell): trocas pontuais no menu e nas rotas do
 *     Vertto (outra página inicial, item renomeado, páginas fora de linha).
 * Preset com `shell: null` e `ajustes: null` muda só o visual: a org segue com o menu e as
 * rotas do Vertto.
 *
 * Por que coluna e não UUID no código: a regra do time é flag por org com default seguro
 * (AGENTS.md §8.8). Um segundo escritório entra no mesmo preset com um UPDATE.
 */
import {
  LayoutDashboard, CheckSquare, Users, Building2, DollarSign, FileSignature,
  MessageCircle, Bot, CalendarPlus, BookOpen, Target, CalendarRange,
} from 'lucide-react';
import type { PermissionKey } from '@/lib/permissions/keys';
import canarioAtticus from '@/assets/atticus/atticus-canario.svg';
import horizontalOlivaAtticus from '@/assets/atticus/atticus-horizontal-oliva.svg';

export type OrgPresetId = 'atticus' | 'atticus_marca' | 'atticus_escritorio';

export type OrgPresetNavItem = {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
};

export type OrgPresetNavGroup = {
  id: string;
  label: string;
  icon: typeof LayoutDashboard;
  items: OrgPresetNavItem[];
};

/** Interface enxuta: o que um escritório que usa só parte do CRM enxerga. */
export type OrgPresetShell = {
  homeRoute: string;
  navGroups: OrgPresetNavGroup[];
  /**
   * Rotas liberadas. Casa a rota exata e tudo abaixo dela (`/deals` libera `/deals/123`).
   * Qualquer outra rota do shell do CRM redireciona para `homeRoute`.
   */
  allowedRoutePrefixes: string[];
  /** Exceções dentro de um prefixo liberado. */
  blockedRoutePrefixes: string[];
  /** Exceções só da rota exata (ex.: a lista `/leads` fechada com `/leads/:id` aberto). */
  blockedExactRoutes?: string[];
  /** Valores de aba de /settings que aparecem (`value` das abas em pages/Settings.tsx). */
  settingsTabs: string[];
};

/** Página fora de linha: some do menu e quem chega por link vai para `destino`. */
export type OrgPresetRotaDesativada = {
  /** Casa a rota exata e tudo abaixo dela (`/operacoes/entregas` pega `/operacoes/entregas/123`). */
  prefixo: string;
  destino: string;
};

/**
 * Trocas no menu e nas rotas do Vertto para um preset SEM shell. A org continua com o menu
 * padrão e os gates de sempre; isto só muda a página inicial, troca itens e tira páginas de
 * linha. Nada é apagado: desligar o ajuste devolve a org ao Vertto padrão.
 */
export type OrgPresetAjustes = {
  /**
   * Página inicial: destino de "/" pós-login e das rotas de dashboard desativadas. Vale só para
   * quem tem `chave`; quem não tem segue o fluxo padrão (Kanban de negócios ou a primeira tela
   * que os seus grupos abrem), nunca uma tela de acesso negado.
   */
  home: { rota: string; chave: PermissionKey };
  /**
   * Itens do menu padrão trocados NO LUGAR, por `href`. Se o `href` novo já existia em outro
   * grupo, esse outro item sai (o item não aparece duas vezes).
   */
  trocasNoMenu: { de: string; para: { name: string; href: string } }[];
  rotasDesativadas: OrgPresetRotaDesativada[];
};

export type OrgPreset = {
  id: OrgPresetId;
  /** Nome exibido no título da aba e no sufixo do PageHeader. */
  brandName: string;
  /** Valor de `data-brand` no <html>; os tokens ficam sob `html[data-brand="…"]`. */
  brandAttr: string;
  /** Folha do Google Fonts carregada só quando o preset está ativo. */
  fontsHref: string;
  /** Favicon servido de /public. */
  faviconHref: string;
  /**
   * Telas de carregamento (splash de entrada, "Verificando autenticação", "Carregando
   * organização"): marca no lugar do "tt" do Vertto e cores fixas. Fixas, e não os tokens do
   * tema, porque essas telas aparecem antes de o `data-brand` estar no <html>.
   */
  loading: { markSrc: string; background: string; accent: string; muted: string };
  /**
   * Logo horizontal para fundo claro, usado nas páginas públicas (agendamento). Fica no
   * preset porque a página pública não passa pelo shell do CRM e não tem outro lugar para
   * saber qual marca desenhar.
   */
  publicLogo: { src: string; width: number; height: number };
  /** Rótulo ao lado do logo na navbar (ex.: "HUB"); `null` = só o logo. */
  navbarTag: string | null;
  /**
   * Nome da IA no Atendimento (lista de conversas, balão, avisos do composer); `null` = "Mia",
   * o nome padrão do Vertto. Ver `useAiName`.
   */
  aiName: string | null;
  /** Menu, rotas e abas próprios; `null` = interface padrão do Vertto, só com a marca trocada. */
  shell: OrgPresetShell | null;
  /** Trocas pontuais no menu e nas rotas do Vertto; só faz sentido com `shell: null`. */
  ajustes: OrgPresetAjustes | null;
};

/** Menu e rotas da Atticus.hub: só IA de atendimento, WhatsApp, cadastro, documentos e agenda. */
const ATTICUS_HUB_SHELL: OrgPresetShell = {
  homeRoute: '/dashboard',
  navGroups: [
    {
      id: 'inicio',
      label: 'Início',
      icon: LayoutDashboard,
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Atividades', href: '/activities', icon: CheckSquare },
        // Links de agendamento (/book/:slug). A página pública já sai com a marca do preset.
        // O item só aparece para quem tem `desempenho.gestao_agenda.manage` (Sidebar).
        { name: 'Agendamento', href: '/agendamento', icon: CalendarPlus },
      ],
    },
    {
      id: 'clientes',
      label: 'Clientes',
      icon: Users,
      items: [
        { name: 'Leads', href: '/leads', icon: Users },
        { name: 'Empresas', href: '/companies', icon: Building2 },
        { name: 'Negócios', href: '/deals', icon: DollarSign },
        { name: 'Documentos', href: '/documentos', icon: FileSignature },
      ],
    },
    {
      id: 'atendimento',
      label: 'Atendimento',
      icon: MessageCircle,
      items: [
        { name: 'WhatsApp', href: '/chat', icon: MessageCircle },
        // Só admin vê (gate em Sidebar.tsx › stableIsRouteEnabled): é a configuração da IA.
        { name: 'IA de atendimento', href: '/agentes', icon: Bot },
      ],
    },
    {
      // Playbooks da operação (docs/specs/materiais-documentacao-playbook.md). Aparece só com a
      // flag `playbooks_enabled` da org e a chave `materiais.playbooks.view` (Sidebar).
      id: 'materiais',
      label: 'Materiais',
      icon: BookOpen,
      items: [
        { name: 'Playbooks', href: '/materiais/playbooks', icon: BookOpen },
      ],
    },
    {
      // Objetivos (OKR). Aparece só com a flag `okr_enabled` da org e as chaves `objetivos.*`
      // (Sidebar). Spec: docs/specs/okr-modulo-objetivos.md.
      id: 'objetivos',
      label: 'Objetivos',
      icon: Target,
      items: [
        { name: 'Objetivos', href: '/objetivos', icon: Target },
        { name: 'Ciclos', href: '/objetivos/ciclos', icon: CalendarRange },
      ],
    },
  ],
  allowedRoutePrefixes: [
    '/',
    '/dashboard',
    '/dashboard-v2', // redirect legado para /dashboard
    '/activities',
    '/agendamento', // links de agendamento + /agendamento/disponibilidade + editor
    '/leads',
    '/companies',
    '/deals',
    '/documentos',
    '/contratos', // mesma tela de /documentos
    '/chat',
    '/agentes',
    '/materiais',
    '/objetivos',
    '/settings',
    '/configuracoes/integracoes',
    '/calls', // transcrição de ligação aberta a partir do negócio
    '/meetings', // transcrição de reunião aberta a partir do negócio
    '/notifications',
    '/profile',
    '/invite',
    '/auth',
    // `/admin/organizations` é do super-admin; `/admin` puro redireciona para /settings.
    '/admin',
  ],
  blockedRoutePrefixes: ['/activities/focus'],
  settingsTabs: [
    'organization',
    'users',
    'custom-fields',
    'negocios',
    'pool',
    'activities',
    'whatsapp-instances',
    'integracoes',
    'contratos',
    'ai',
    'notifications',
  ],
};

/** Marca Atticus (brandbook Direção Oliva v2.0), comum aos dois presets do escritório. */
const ATTICUS_BRAND = {
  brandAttr: 'atticus',
  fontsHref:
    'https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,300;9..144,500&family=IBM+Plex+Mono:wght@400;500&display=swap',
  faviconHref: '/atticus-favicon.svg',
  // Canário em Oliva sobre Papel (brandbook: símbolo para avatar, favicon e marca d'água).
  loading: { markSrc: canarioAtticus, background: '#F7F5F0', accent: '#22300B', muted: '#5C5A52' },
  // Lockup horizontal em Oliva (o original do brandbook é Papel, para fundo escuro).
  publicLogo: { src: horizontalOlivaAtticus, width: 156, height: 28 },
} satisfies Partial<OrgPreset>;

/** Org Atticus.hub (fd4c73ab): marca e interface enxuta. */
const ATTICUS: OrgPreset = {
  ...ATTICUS_BRAND,
  id: 'atticus',
  brandName: 'Atticus.hub',
  navbarTag: 'HUB',
  aiName: null,
  shell: ATTICUS_HUB_SHELL,
  ajustes: null,
};

/**
 * Org Atticus (pedido do Flávio, 29/10/2026): o Relatório Atticus vira o "Dashboard" e a
 * página inicial, o dashboard padrão e os Relatórios personalizados saem de linha, e Operações
 * fica só com a Carteira (e o Cancelamento, que segue a flag `contract_lifecycle_enabled`).
 *
 * A rota continua `/relatorio-atticus`: links e favoritos seguem valendo. Para outra org
 * ganhar o mesmo desenho, basta um preset com `ajustes` equivalentes.
 */
const ATTICUS_MARCA_AJUSTES: OrgPresetAjustes = {
  home: { rota: '/relatorio-atticus', chave: 'desempenho.relatorio_atticus.view' },
  trocasNoMenu: [
    { de: '/dashboard', para: { name: 'Dashboard', href: '/relatorio-atticus' } },
  ],
  rotasDesativadas: [
    // Dashboard padrão e as rotas antigas dele.
    { prefixo: '/dashboard', destino: '/relatorio-atticus' },
    { prefixo: '/dashboard-legado', destino: '/relatorio-atticus' },
    { prefixo: '/dashboard-v2', destino: '/relatorio-atticus' },
    // Relatórios personalizados (adendo do Flávio, 29/10/2026), com /metricas/:id e os
    // redirects antigos do laboratório.
    { prefixo: '/metricas', destino: '/relatorio-atticus' },
    { prefixo: '/lab/analysis-v0', destino: '/relatorio-atticus' },
    // Operações: tudo menos Carteira (/operacoes/contratos) e Cancelamento.
    { prefixo: '/operacoes/dashboard', destino: '/operacoes/contratos' },
    { prefixo: '/operacoes/projetos', destino: '/operacoes/contratos' },
    { prefixo: '/operacoes/entregas', destino: '/operacoes/contratos' },
    { prefixo: '/operacoes/tarefas', destino: '/operacoes/contratos' },
    { prefixo: '/operacoes/sprints', destino: '/operacoes/contratos' },
    { prefixo: '/operacoes/importar', destino: '/operacoes/contratos' },
    // Redirects legados do portal que levariam a uma das páginas acima.
    { prefixo: '/operacoes/quadro', destino: '/operacoes/contratos' },
    { prefixo: '/operacoes/portfolios', destino: '/operacoes/contratos' },
    { prefixo: '/operacoes/meu-trabalho', destino: '/operacoes/contratos' },
  ],
};

/**
 * Org "Atticus" (08ae88ea, operação comercial): a marca do escritório e os ajustes acima.
 * O resto do menu, das rotas, o telefone e os tours seguem os do Vertto.
 */
const ATTICUS_MARCA: OrgPreset = {
  ...ATTICUS_BRAND,
  id: 'atticus_marca',
  brandName: 'Atticus',
  navbarTag: null,
  // A IA da operação comercial da Atticus é a Attia (pedido do Flávio, 12/10/2026).
  aiName: 'Attia',
  shell: null,
  ajustes: ATTICUS_MARCA_AJUSTES,
};


/**
 * Org de um escritório que compra pastas da Atticus (rateio e entrega, spec §8 e §10). Nasce
 * pelo `rateio_conta_provisionar`, do zero: não herda flag nem menu da Atticus. O escritório só
 * recebe e trabalha as pastas na pipeline de entrega, registra atividades e cuida da equipe.
 * Leads e empresas ficam de fora: a pasta chega como negócio, com os dados do lead mascarados
 * até o pagamento, e a tela de lead mostraria o cadastro por outro caminho.
 */
// O escritório não vê o Kanban de negócios nem a página do negócio (pedido de 30/10/2026): as
// pastas ficam em `/pastas` (quadro + gaveta, src/pages/pastas-escritorio). Qualquer `/deals/*`
// cai na home pela guarda do preset.
const ATTICUS_ESCRITORIO_SHELL: OrgPresetShell = {
  homeRoute: '/pastas',
  navGroups: [
    {
      id: 'pastas',
      label: 'Pastas',
      icon: DollarSign,
      items: [
        { name: 'Pastas', href: '/pastas', icon: DollarSign },
        { name: 'Atividades', href: '/activities', icon: CheckSquare },
      ],
    },
  ],
  allowedRoutePrefixes: [
    '/',
    '/pastas',
    '/activities',
    // Pasta paga vira negócio comum (03/11): página do negócio e do lead dela. Pasta não paga
    // volta para a gaveta em /pastas (`PastaPagaGuard`). Kanban, lista e criação seguem fora.
    '/deals',
    '/leads',
    '/settings',
    '/notifications',
    '/profile',
    '/invite',
    '/auth',
    // `/admin/organizations` é do super-admin; `/admin` puro redireciona para /settings.
    '/admin',
  ],
  blockedRoutePrefixes: [
    '/activities/focus',
    '/deals/kanban',
    '/deals/list',
    '/deals/forecast',
    '/deals/import',
    '/deals/new',
    '/leads/import',
    '/leads/new',
  ],
  // `/deals` (vira o Kanban) e a lista de leads ficam fechados; as páginas de cada um, abertas.
  blockedExactRoutes: ['/deals', '/leads'],
  settingsTabs: ['organization', 'users', 'notifications'],
};

const ATTICUS_ESCRITORIO: OrgPreset = {
  ...ATTICUS_BRAND,
  id: 'atticus_escritorio',
  brandName: 'Atticus',
  navbarTag: null,
  aiName: null,
  shell: ATTICUS_ESCRITORIO_SHELL,
  ajustes: null,
};

const ORG_PRESETS: Record<OrgPresetId, OrgPreset> = {
  atticus: ATTICUS,
  atticus_marca: ATTICUS_MARCA,
  atticus_escritorio: ATTICUS_ESCRITORIO,
};

/** Valor desconhecido (build antigo, typo no banco) cai em `null`: Vertto padrão, nunca meio preset. */
export function getOrgPreset(uiPreset: string | null | undefined): OrgPreset | null {
  if (!uiPreset || !Object.prototype.hasOwnProperty.call(ORG_PRESETS, uiPreset)) return null;
  return ORG_PRESETS[uiPreset as OrgPresetId];
}

/**
 * O preset precisa ser conhecido ANTES de a org carregar: a splash de entrada aparece enquanto
 * as orgs ainda estão sendo buscadas, e sem isto o "tt" do Vertto piscaria antes do canário.
 * Guardamos o par (org, preset) da última visita e só usamos se a org guardada para abrir
 * (`current_org_id`, a mesma que o OrganizationContext vai carregar) for a mesma.
 */
const CHAVE_PRESET_GUARDADO = 'org_ui_preset_cache';

export function rememberOrgPreset(orgId: string, uiPreset: string | null | undefined): void {
  try {
    localStorage.setItem(CHAVE_PRESET_GUARDADO, JSON.stringify({ orgId, uiPreset: uiPreset ?? null }));
  } catch {
    // Storage bloqueado (aba anônima, política do navegador): só perde o palpite da splash.
  }
}

export function getRememberedOrgPreset(orgIdToOpen: string | null): OrgPreset | null {
  if (!orgIdToOpen) return null;
  try {
    const raw = localStorage.getItem(CHAVE_PRESET_GUARDADO);
    if (!raw) return null;
    const { orgId, uiPreset } = JSON.parse(raw) as { orgId?: string; uiPreset?: string | null };
    return orgId === orgIdToOpen ? getOrgPreset(uiPreset) : null;
  } catch {
    return null;
  }
}

function matchesPrefix(pathname: string, prefix: string): boolean {
  if (prefix === '/') return pathname === '/';
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Rota fora de linha pelos `ajustes` do preset: devolve o destino, ou `null` se a rota vale. */
export function rotaDesativadaPorPreset(preset: OrgPreset | null, pathname: string): string | null {
  const regra = preset?.ajustes?.rotasDesativadas.find((r) => matchesPrefix(pathname, r.prefixo));
  return regra?.destino ?? null;
}

/** Preset sem `shell` e sem `ajustes` não restringe rota nenhuma. */
export function isRouteAllowedByPreset(preset: OrgPreset, pathname: string): boolean {
  if (rotaDesativadaPorPreset(preset, pathname)) return false;
  const { shell } = preset;
  if (!shell) return true;
  if (shell.blockedRoutePrefixes.some((p) => matchesPrefix(pathname, p))) return false;
  if (shell.blockedExactRoutes?.some((p) => pathname === p || pathname === `${p}/`)) return false;
  return shell.allowedRoutePrefixes.some((p) => matchesPrefix(pathname, p));
}

type ItemDeMenu = { name: string; href: string; current?: boolean };

/**
 * Aplica os `ajustes` do preset ao menu padrão: troca itens no lugar, tira o duplicado que a
 * troca criaria e remove as páginas fora de linha. Grupo que fica vazio sai. Sem ajustes,
 * devolve o menu como veio.
 */
export function aplicarAjustesNoMenu<I extends ItemDeMenu, G extends { items: I[] }>(
  grupos: G[],
  preset: OrgPreset | null,
  pathname: string,
): G[] {
  const ajustes = preset?.ajustes;
  if (!ajustes) return grupos;
  const trocas = new Map(ajustes.trocasNoMenu.map((t) => [t.de, t.para]));
  const hrefsNovos = new Set(ajustes.trocasNoMenu.map((t) => t.para.href));
  return grupos
    .map((grupo) => ({
      ...grupo,
      items: grupo.items.flatMap((item): I[] => {
        const para = trocas.get(item.href);
        if (para) {
          return [{ ...item, name: para.name, href: para.href, current: isNavItemActive(pathname, para.href) }];
        }
        if (hrefsNovos.has(item.href)) return [];
        if (rotaDesativadaPorPreset(preset, item.href)) return [];
        return [item];
      }),
    }))
    .filter((grupo) => grupo.items.length > 0);
}

export function isNavItemActive(pathname: string, href: string): boolean {
  return matchesPrefix(pathname, href);
}
